import { Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { IBadge } from '@clickersphere/core/entities/IBadge';
import { BadgeColorEnum } from '@clickersphere/core/enums/BadgeColorEnum';
import { useBadges } from '../../../../app/hooks/badge/useBadges';
import { useCreateBadge } from '../../../../app/hooks/badge/useCreateBadge';
import { useDeleteBadge } from '../../../../app/hooks/badge/useDeleteBadge';
import { useUpdateBadge } from '../../../../app/hooks/badge/useUpdateBadge';
import useToast from '../../../../app/hooks/useToast';
import { useStore } from '../../../../app/store';
import BadgeChip from '../../Badge/BadgeChip';
import ColorPicker from '../../Badge/ColorPicker';
import { Button } from '../../Button';
import EmptyState from '../../EmptyState';
import { Input } from '../../Input';
import Loader from '../../Loader';

interface IFormState {
  mode: 'create' | 'edit';
  id?: string;
  name: string;
  color: BadgeColorEnum;
}

const EMPTY_FORM: IFormState = {
  mode: 'create',
  name: '',
  color: BadgeColorEnum.BLUE,
};

export default function BadgeSettingsPanel() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { badges, isBadgesLoading } = useBadges();
  const { createBadgeAsync, createBadgeIsPending } = useCreateBadge();
  const { updateBadgeAsync, updateBadgeIsPending } = useUpdateBadge();
  const { deleteBadgeAsync } = useDeleteBadge();

  const { handleOpenConfirmationModal, handleCloseConfirmationModal } =
    useStore(
      useShallow((state) => ({
        handleOpenConfirmationModal: state.modal.handleOpenConfirmationModal,
        handleCloseConfirmationModal: state.modal.handleCloseConfirmationModal,
      })),
    );

  const [form, setForm] = useState<IFormState | null>(null);

  const isFormBusy = createBadgeIsPending || updateBadgeIsPending;

  function startCreate() {
    setForm({ ...EMPTY_FORM });
  }

  function startEdit(badge: IBadge) {
    setForm({
      mode: 'edit',
      id: badge.id,
      name: badge.name,
      color: badge.color,
    });
  }

  function cancelForm() {
    setForm(null);
  }

  async function submitForm() {
    if (!form) return;
    const name = form.name.trim();
    if (!name) return;
    try {
      if (form.mode === 'create') {
        await createBadgeAsync({ name, color: form.color });
        toast(t('badge.toast.created'), 'success');
      } else if (form.id) {
        await updateBadgeAsync({
          id: form.id,
          data: { name, color: form.color },
        });
        toast(t('badge.toast.updated'), 'success');
      }
      setForm(null);
    } catch {
      toast(
        form.mode === 'create'
          ? t('badge.toast.error.create')
          : t('badge.toast.error.update'),
        'error',
      );
    }
  }

  function confirmDelete(badge: IBadge) {
    handleOpenConfirmationModal({
      title: t('badge.modal.delete.title'),
      text: t('badge.modal.delete.text', { name: badge.name }),
      secondaryText: t('badge.modal.delete.secondaryText'),
      confirmButtonText: t('badge.modal.delete.confirm'),
      cancelButtonText: t('badge.modal.delete.cancel'),
      titlePosition: 'center',
      danger: true,
      onCancel: handleCloseConfirmationModal,
      onClose: handleCloseConfirmationModal,
      onConfirm: async () => {
        handleCloseConfirmationModal();
        try {
          await deleteBadgeAsync(badge.id);
          toast(t('badge.toast.deleted'), 'success');
        } catch {
          toast(t('badge.toast.error.delete'), 'error');
        }
      },
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
        <p className="text-text-secondary text-sm">
          {t('badge.settings.description')}
        </p>
        {!form && (
          <Button
            color="outline"
            labelPath="badge.settings.newBadge"
            className="w-full sm:w-auto"
            onClick={startCreate}
          />
        )}
      </div>

      {form && (
        <div className="border-border-default bg-surface-elevated dark:bg-surface-card flex flex-col gap-3 rounded-lg border p-3">
          <h3 className="text-text-primary text-sm font-semibold">
            {form.mode === 'create'
              ? t('badge.settings.form.titleCreate')
              : t('badge.settings.form.titleEdit')}
          </h3>
          <Input
            name="badgeName"
            value={form.name}
            onChange={(e) =>
              setForm((prev) =>
                prev ? { ...prev, name: e.target.value } : prev,
              )
            }
            placeholder={t('badge.combobox.namePlaceholder')}
            maxLength={50}
          />
          <ColorPicker
            value={form.color}
            onChange={(color) =>
              setForm((prev) => (prev ? { ...prev, color } : prev))
            }
          />
          <div className="flex gap-2">
            <Button
              color="ghost"
              labelPath="badge.combobox.cancel"
              onClick={cancelForm}
              disabled={isFormBusy}
            />
            <Button
              color="primary"
              labelPath={
                form.mode === 'create'
                  ? 'badge.combobox.confirm'
                  : 'badge.settings.form.save'
              }
              onClick={submitForm}
              disabled={!form.name.trim() || isFormBusy}
              isLoading={isFormBusy}
            />
          </div>
        </div>
      )}

      {isBadgesLoading && (
        <div className="flex justify-center py-4">
          <Loader className="h-6 w-6" />
        </div>
      )}

      {!isBadgesLoading && badges.length === 0 && (
        <EmptyState>
          <p>{t('badge.settings.empty')}</p>
        </EmptyState>
      )}

      {!isBadgesLoading && badges.length > 0 && (
        <ul className="flex flex-col divide-y divide-(--color-border)">
          {badges.map((badge) => (
            <li
              key={badge.id}
              className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex flex-1 items-center gap-3">
                <BadgeChip badge={badge} size="sm" removable={false} />
                <span className="text-text-tertiary text-xs">
                  {t('badge.settings.usageCount', {
                    count: badge.usageCount,
                  })}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  color="ghost"
                  className="h-10 w-10 px-0! sm:w-auto sm:px-4!"
                  aria-label={t('badge.settings.actions.edit')}
                  onClick={() => startEdit(badge)}
                >
                  <Pencil1Icon className="h-4 w-4" />
                </Button>
                <Button
                  color="danger"
                  className="h-10 w-10 px-0! sm:w-auto sm:px-4!"
                  aria-label={t('badge.settings.actions.delete')}
                  onClick={() => confirmDelete(badge)}
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
